import _ from 'underscore';

import {assertAndContinue} from 'Asserts';
import {Grid, isAccessibleGridCell, GridCell} from 'constants/SampleConstants';
import {Position} from 'samples/SampleUtil';
import {PLATE_SCHEMA_TYPE} from 'samples/storables/StorableSchemaConstants';
import APIUtil from 'util/APIUtil';

export enum OrderingStrategy {
  ROW_FIRST = 'ROW_FIRST',
  COLUMN_FIRST = 'COLUMN_FIRST',
}

interface PositionsByGrid {
  [gridId: string]: Array<Position>;
}

interface GridsById {
  [id: string]: Grid | null;
}

/**
 * Get cells that are empty, or have an empty container of the correct schema.
 * From the perspective of a user, empty containers are typically still desired to be treated as available,
 * for example in a well plate where every cell always has a container.
 */
function isCellAvailable({
  cell,
  containerSchemaId,
}: {
  cell: GridCell | null;
  containerSchemaId: string;
}): boolean {
  return (
    !cell ||
    (isAccessibleGridCell(cell) &&
      cell.container.containerSchemaId === containerSchemaId &&
      cell.container.contents.length === 0)
  );
}

function getPositionEnumerationOrder({
  grid,
  orderingStrategy,
}: {
  grid: Grid;
  orderingStrategy: OrderingStrategy;
}): Array<Position> {
  const positions: Array<Position> = [];
  if (orderingStrategy === OrderingStrategy.ROW_FIRST) {
    for (const rowIndex of _.range(grid.storableSchema.height)) {
      for (const columnIndex of _.range(grid.storableSchema.width)) {
        positions.push({rowIndex, columnIndex});
      }
    }
  } else if (orderingStrategy === OrderingStrategy.COLUMN_FIRST) {
    for (const columnIndex of _.range(grid.storableSchema.width)) {
      for (const rowIndex of _.range(grid.storableSchema.height)) {
        positions.push({rowIndex, columnIndex});
      }
    }
  } else {
    assertAndContinue(false, 'Unexpected ordering strategy');
  }
  return positions;
}

/**
 * Given a grid, get the next positions that are either empty, or occupied by an empty container
 * of the given schema.
 */
export function getNextAvailablePositions({
  grid,
  containerSchemaId,
  orderingStrategy,
}: {
  grid: Grid | null;
  containerSchemaId: string;
  orderingStrategy: OrderingStrategy;
}): Array<Position> {
  if (!grid) {
    return [];
  }
  return getPositionEnumerationOrder({grid, orderingStrategy}).filter(({rowIndex, columnIndex}) =>
    isCellAvailable({cell: grid.cells[rowIndex][columnIndex], containerSchemaId})
  );
}

export function getAvailablePositionsByGridId({
  gridIds,
  gridsById,
  containerSchemaId,
  orderingStrategy,
}: {
  gridIds: Array<string>;
  gridsById: GridsById;
  containerSchemaId: string;
  orderingStrategy: OrderingStrategy;
}): PositionsByGrid {
  const availablePositionsByGridId: PositionsByGrid = {};
  for (const gridId of gridIds) {
    availablePositionsByGridId[gridId] = getNextAvailablePositions({
      grid: gridsById[gridId],
      containerSchemaId,
      orderingStrategy,
    });
  }
  return availablePositionsByGridId;
}

/**
 * Given a grid, get the next positions that are completely empty.
 */
export function getNextEmptyPositions({
  grid,
  orderingStrategy,
}: {
  grid: Grid | null;
  orderingStrategy: OrderingStrategy;
}): Array<Position> {
  if (!grid) {
    return [];
  }
  return getPositionEnumerationOrder({grid, orderingStrategy}).filter(({rowIndex, columnIndex}) => {
    const cell = grid.cells[rowIndex][columnIndex];
    if (APIUtil.isPlate(grid.id) && grid.storableSchema.plateSchemaType === PLATE_SCHEMA_TYPE.FIXED_PLATE) {
      // In a well plate, empty positions are positions that have no contents.
      // An empty cell can still have a container, because for a well plate, every position has a container
      // as soon as the plate is created.
      return !cell || (isAccessibleGridCell(cell) && cell.container.contents.length === 0);
    } else {
      // In a box or a matrix plate, an empty position should never have a container, not even an empty one.
      return !cell || cell.container == null;
    }
  });
}

export function getEmptyPositionsByGridId({
  gridIds,
  gridsById,
  orderingStrategy,
}: {
  gridIds: Array<string>;
  gridsById: GridsById;
  orderingStrategy: OrderingStrategy;
}): PositionsByGrid {
  const emptyPositionsByGridId: PositionsByGrid = {};
  for (const gridId of gridIds) {
    emptyPositionsByGridId[gridId] = getNextEmptyPositions({
      grid: gridsById[gridId],
      orderingStrategy,
    });
  }
  return emptyPositionsByGridId;
}
